import { Check } from "lucide-react";
import type { ChecklistItem } from "@/lib/blocks";
import { cn } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";
import { useBlockActions } from "./BlockRenderer";

function parseItems(body: string): ChecklistItem[] {
  const out: ChecklistItem[] = [];
  for (const line of body.split("\n")) {
    const m = line.match(/^\s*[-*+]\s+\[([ xX])\]\s+(.*)$/);
    if (m) out.push({ text: m[2], checked: m[1] !== " " });
  }
  return out;
}

export function Checklist({ id, props, body }: { id: string; props: Record<string, unknown>; body: string }) {
  const { onToggleCheck, pending } = useBlockActions();
  const items: ChecklistItem[] = Array.isArray(props.items)
    ? (props.items as ChecklistItem[]).map((i) => ({ text: String(i?.text ?? ""), checked: !!i?.checked }))
    : parseItems(body);
  const interactive = !!onToggleCheck;
  const busy = pending?.has(id) ?? false;
  const done = items.filter((i) => i.checked).length;

  if (items.length === 0) {
    return <div className="rounded-xl border border-dashed px-4 py-3 text-sm text-muted-foreground">Empty checklist</div>;
  }

  return (
    <div className="rounded-xl border bg-card">
      <ul className="divide-y divide-border/70">
        {items.map((item, index) => (
          <li key={index}>
            <label
              className={cn(
                "flex items-start gap-3 px-4 py-2.5 text-[0.95rem] leading-relaxed",
                interactive ? "cursor-pointer hover:bg-muted/40" : "cursor-default",
                busy && "opacity-60",
              )}
            >
              <span className="relative mt-[0.3rem] inline-flex size-[18px] shrink-0 items-center justify-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={item.checked}
                  disabled={!interactive || busy}
                  onChange={(e) => onToggleCheck?.(id, index, e.target.checked)}
                  aria-label={item.text}
                />
                <span
                  className={cn(
                    "absolute inset-0 rounded-[5px] border transition-colors peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/40",
                    item.checked ? "border-primary bg-primary" : "border-input bg-card",
                  )}
                />
                {item.checked && <Check className="relative size-3.5 text-primary-foreground" strokeWidth={3} />}
              </span>
              <span className={cn("min-w-0 flex-1", item.checked && "text-muted-foreground line-through decoration-border")}>
                <Markdown inline>{item.text}</Markdown>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t px-4 py-1.5 text-xs text-muted-foreground">
        <span>
          {done} of {items.length} done
        </span>
        <span className="h-1 w-24 overflow-hidden rounded-full bg-muted">
          <span className="block h-full rounded-full bg-primary transition-all" style={{ width: `${(100 * done) / items.length}%` }} />
        </span>
      </div>
    </div>
  );
}
